import type { Lang } from '@/data/categories';
import { homeContent } from '@/data/home';
import { articles } from '@/data/articles';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ContentSections({ lang }: { lang: Lang }) {
  const c = homeContent[lang];

  return (
    <>
      {/* What is */}
      <section className="bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{c.whatIs.h2}</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{c.whatIs.copy}</p>
              <a
                href="#guides"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('guides')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
              >
                {c.whatIs.cta}
                <i className="ri-arrow-right-line text-base"></i>
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {['ri-table-line', 'ri-store-2-line', 'ri-ship-line', 'ri-camera-lens-line'].map((icon, i) => (
                <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${icon} text-lg`}></i>
                  </span>
                  <p className="mt-3 text-sm font-medium text-foreground-800">{c.how.steps[i]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular resources */}
      <section id="guides" className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{c.popular.h2}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">{c.popular.sub}</p>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {articles.map((a) => (
            <ArticleCard key={a.slug} article={a} lang={lang} />
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <h2 className="text-center text-2xl font-bold text-foreground-950 md:text-3xl">{c.how.h2}</h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {c.how.steps.map((step, i) => (
              <div key={i} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-background-50">
                  {i + 1}
                </span>
                <p className="mt-3 text-sm font-medium leading-snug text-foreground-800">{step}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a
              href="#categories"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              {c.how.cta}
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}